import { html, LookupItem, markText, render } from '@cumu/shared';
import type { AutoCompleteElement } from '@github/auto-complete-element';

export interface AutoCompleteResult {
  suggestions: LookupItem[];
}

export interface AutoCompleteProvideEventDetail {
  relatedTarget: HTMLInputElement;
  provide: (result: Promise<AutoCompleteResult>) => void;
}

export class AutoCompleteBehaviorElement extends HTMLElement {
  declare disconnectController: AbortController;

  connectedCallback() {
    this.disconnectController = new AbortController();
    const signal = this.disconnectController.signal;

    const autocomplete = this.closest('auto-complete');
    if (!autocomplete) {
      return;
    }

    if (autocomplete.src === '/use-events') {
      autocomplete.fetchResult = dispatchAutoCompleteProvide;
    }

    autocomplete.inputElement?.addEventListener(
      'blur',
      () => {
        // Close the dropdown on blur.
        // Timeout ensures this doesn't interfere with blurs that occur when selecting a suggestion.
        setTimeout(() => {
          if (autocomplete.open) {
            autocomplete.open = false;
          }
        }, 300); // do not reduce without testing quickly toggling between autocomplete suggestions with mouse.
      },
      { signal }
    );

    autocomplete.addEventListener(
      'auto-complete-change',
      () => {
        if (autocomplete.inputElement?.value.endsWith(' ')) {
          // trigger input event to fetch new suggestions
          autocomplete.inputElement.dispatchEvent(
            new CustomEvent('input', { bubbles: true })
          );
        }
      },
      { signal }
    );

    if (this.firstElementChild instanceof HTMLInputElement) {
      const input = this.firstElementChild;
      autocomplete.addEventListener(
        'combobox-commit',
        ({ target }: Event) => {
          if (!(target instanceof HTMLElement)) {
            return;
          }
          const { id, title } = JSON.parse(
            target.getAttribute('data-autocomplete-data')!
          );
          input.value = id;
          input.dataset.displayValue = title;
          target.setAttribute('data-autocomplete-value', title);
        },
        { signal, capture: true }
      );

      autocomplete.inputElement?.addEventListener(
        'change',
        () => {
          const current = autocomplete.inputElement!.value;
          if (current !== input.dataset.displayValue) {
            autocomplete.inputElement!.value = '';
            input.value = '';
            input.dataset.displayValue = '';
          }
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.disconnectController.abort();
  }
}

async function dispatchAutoCompleteProvide(
  this: AutoCompleteElement,
  _url: URL
): Promise<string> {
  const provided: Promise<AutoCompleteResult>[] = [];
  const provide = (result: Promise<AutoCompleteResult>) => {
    provided.push(result);
  };
  const relatedTarget = this.inputElement!;
  this.dispatchEvent(
    new CustomEvent<AutoCompleteProvideEventDetail>('auto-complete-provide', {
      bubbles: true,
      detail: { relatedTarget, provide }
    })
  );
  const results = await Promise.all(provided);
  return render(
    results
      .flatMap(x => x.suggestions)
      .map(
        ({ id, title }) =>
          html`<li
            role="option"
            class="autocomplete-item"
            data-autocomplete-value="${id}"
            data-autocomplete-data="${JSON.stringify({ id, title })}"
          >
            ${markText(title, relatedTarget.value)}
          </li>`
      )
  );
}

declare global {
  interface Window {
    AutoCompleteBehaviorElement: typeof AutoCompleteBehaviorElement;
  }
  interface HTMLElementTagNameMap {
    'auto-complete-behavior': AutoCompleteBehaviorElement;
  }

  interface ElementEventMap {
    'auto-complete-provide': CustomEvent<AutoCompleteProvideEventDetail>;
  }

  interface WindowEventMap {
    'auto-complete-provide': CustomEvent<AutoCompleteProvideEventDetail>;
  }
}

if (!window.customElements.get('auto-complete-behavior')) {
  window.AutoCompleteBehaviorElement = AutoCompleteBehaviorElement;
  window.customElements.define(
    'auto-complete-behavior',
    AutoCompleteBehaviorElement
  );
}
